import microuniversoApi from "app/api/microuniversoApi";
import { Breadcrumb2Container } from "app/components/breadcrumb2/Breadcrumb2Container";
import { useAppSelector } from "app/redux/hooks";
import globalMU from "globalMU";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Ofertas } from "./ofertas";
import { RespostaOfertas } from "app/types/produto/RespostaOfertas";
import { Parametro, ParametroPorItem } from "app/types/Parametro";
import { obterParametros, obterParametrosPorOidItem } from "app/use-cases/parametro/obterParametros";
import { IItemOrcamento } from "app/types/IItemOrcamento";
import { tratamentoErroPadrao } from "app/use-cases";
import { Dict } from "app/utils/Dict";
import { useMenuConsultaProduto } from "app/components/menu-consulta-produtos/useMenuConsultaProduto";

export type OfertasContainerProps = {
    item: IItemOrcamento,
    onVoltar: () => void,
    onRemover: () => void,
    passos: Dict<string,() => void>,
    foraOrcamento: boolean
};

export const OfertasContainer = (props: OfertasContainerProps) => {
    const isMounted = useRef(true);
    const orcamento = useAppSelector((store) => store.orcamento);
    const [parametrosPorFilial, setParametrosPorFilial] = useState<ParametroPorItem[]>([]) // 456099

    const [dadosDeOferta, setDadosDeOferta] = useState<RespostaOfertas>()
    const [parametros, setParametros] = useState<Parametro[]>([])

    const passosInterno = useRef<Dict<string, () => void>>(new Dict(Object.assign({}, props.passos.map)))
    useMenuConsultaProduto(new Dict(Object.assign({}, props.passos.map)), props.onVoltar, props.item, props.foraOrcamento)

    const valorParametro = useCallback((nome: string) => 
        parametros.find(p => p.Nome == nome)?.Valor, [parametros])
    
    const valorParametroPorFilial = useCallback((nome: string) => 
        parametrosPorFilial.find((p) => p.Nome == nome)?.Valor, [parametrosPorFilial]); // 456099

    const atualizarParametros = useCallback(async () => {
        try {
            const [parametrosGeral, parametrosFilial] = await Promise.all([
                obterParametros({
                    nomes: [
                        "TRABALHA_OFERTA_CLIENTES_CADASTRADOS",
                        "GRUPO_OFERTAS", // 447851
                        "LISTA_OFERTAS_POR_FILIAL",
                    ]
                }),
                obterParametrosPorOidItem({
                    nomes: ["OFERTA_VALOR_DIFERENCIADO_QUANTIDADE_VENDIDA_ATACADO", // 456099
                    ],
                    oidItem: orcamento.OidFilialOrcamento
                })
            ]);

            if (isMounted.current) {
                setParametros(parametrosGeral);
                setParametrosPorFilial(parametrosFilial);
            }
        } catch (error) {
            console.error("Erro ao atualizar parâmetros:", error);
        }
    }, [orcamento.OidFilialOrcamento]);

    const onObterDadosOfertas = useCallback(async () => {
        try {
            globalMU.blockUI();
            const ofertas = await microuniversoApi.Produto.dadosOfertas(orcamento.IdentificadorOrcamento, {
                CodigoProduto: props.item.Codigo,
                NumeroPagina: 1,
                TamanhoPagina: 20 
            });

            if (!isMounted.current) return;

            if (!isMounted.current) return;

            !("HasErrors" in ofertas) ? 
            setDadosDeOferta(ofertas) : 
            (setDadosDeOferta({}), onVoltar())
        }
        catch (e) {
            if (isMounted.current) {
                tratamentoErroPadrao(e);
                onVoltar()
            }
        }
        finally {
            globalMU.unblockUI();
        }
    }, [orcamento.IdentificadorOrcamento, props.item.Codigo]);

    const onPesquisarOfertasPaginado = useCallback(async (numeroPagina: number) => {
        try {
            globalMU.blockUI();
            const ofertas = await microuniversoApi.Produto.listarOfertasProduto(orcamento.IdentificadorOrcamento, {
                CodigoProduto: props.item.Codigo,
                NumeroPagina: numeroPagina,
                TamanhoPagina: 20 
            });
            
            if (isMounted.current) {
                setDadosDeOferta(prev => prev ? {...prev, Dados: ofertas} : undefined);
            }
        }
        catch (e) {
            if (isMounted.current) {
                tratamentoErroPadrao(e);
                onVoltar()
            }
        }
        finally {
            globalMU.unblockUI();
        }
    }, [orcamento.IdentificadorOrcamento, props.item.Codigo]);

    const onPassoSelecionado = useCallback((passo: string) => {
        props.onRemover()
        passosInterno.current.get(passo)()
    }, [props.onRemover]);

    const onVoltar = useCallback(() => {
        props.onRemover()
        props.onVoltar()
    }, [props.onRemover, props.onVoltar]);

    useEffect(() => {
        isMounted.current = true;
        
        passosInterno.current.set("Ofertas",() => {});
        onObterDadosOfertas();
        atualizarParametros();

        return () => {
            isMounted.current = false;
        };
    }, [onObterDadosOfertas, atualizarParametros]);

    const produtoCodigo = props.item.CodigoProdutoExibicao?.trimEnd() || props.item.CodigoProduto?.trimEnd();

    if (!parametros?.length || !parametrosPorFilial?.length || !dadosDeOferta?.Dados) {
        return null;
    }

    return (
        <>
            <Breadcrumb2Container
                passos={passosInterno.current.keysToList()}
                id="OfertasContainer"       
                exibirBotaoLimpar={false}
                exibirBotaoVoltar={true}
                onPassoSelecionado={onPassoSelecionado}
                onBotaoVoltarClicado={onVoltar}
            />
            <Ofertas
                produto={produtoCodigo || ''}
                descricao={props.item.Descricao}
                dadosOfertas={dadosDeOferta}
                porGrupoDeOfertas={valorParametro("GRUPO_OFERTAS") == "S"}
                tratamentoDiferenciadoAtacado={valorParametroPorFilial("OFERTA_VALOR_DIFERENCIADO_QUANTIDADE_VENDIDA_ATACADO") == "S"}
                permiteVisualizacaPorFilial={valorParametro("LISTA_OFERTAS_POR_FILIAL") == "S"}
                permiteVisualizacaoOfertasClientes={valorParametro("TRABALHA_OFERTA_CLIENTES_CADASTRADOS") == "S"}
                onPesquisarPaginado={onPesquisarOfertasPaginado}
            />
        </>
    )
}